import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { formatMoney, fmtDate, fmtDateTime, durationHours } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ExportMenu from "@/components/ExportMenu";

export default function InvoiceDetailPage() {
    const { id } = useParams();
    const qc = useQueryClient();
    const { data, isLoading, refetch } = useQuery({ queryKey: ["invoice", id], queryFn: () => apiGet(`/invoices/${id}`) });
    const { data: bookingDetail } = useQuery({ queryKey: ["invoice-booking", data?.invoice?.booking_id], queryFn: () => apiGet(`/bookings/${data.invoice.booking_id}`), enabled: !!data?.invoice?.booking_id });
    const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: () => apiGet("/locations"), enabled: !!bookingDetail?.room?.location_id });
    const { data: clientActivity = [] } = useQuery({ queryKey: ["client-activity", data?.client?.id], queryFn: () => apiGet(`/clients/${data.client.id}/interactions`, { limit: 8 }), enabled: !!data?.client?.id });
    if (isLoading) return <div className="p-6">Loading…</div>;
    if (!data) return <div className="p-6">Not found</div>;
    const { invoice, payments, client } = data;
    const location = locations.find((item) => item.id === bookingDetail?.room?.location_id);

    const act = async (path, body) => {
        try { await apiPost(path, body || {}); toast.success("Recorded"); refetch(); qc.invalidateQueries({ queryKey: ["invoices"] }); } catch { toast.error("Failed"); }
    };

    const paymentLabel = (p) => {
        if (p.is_refund) return "Refund";
        if (p.is_deposit) return "Deposit";
        return "Payment";
    };

    const lineItemExportColumns = [
        { header: "Description", key: "description" },
        { header: "Qty", key: "qty" },
        { header: "Unit", key: "unit" },
        { header: "Total", key: "total" },
    ];
    const lineItemExportRows = invoice.line_items.map((li) => ({
        description: li.description,
        qty: li.qty,
        unit: formatMoney(li.unit_price),
        total: formatMoney(li.total),
    }));

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <PageHeader title={invoice.number} subtitle={`${client?.name} · ${location?.name || "Location not set"} · ${bookingDetail?.room?.name || "Room not set"} · due ${fmtDate(invoice.due_date)}`} eyebrow="Business / Invoice" backTo="/invoices" backLabel="Back to Invoices" testId="invoice-detail-header" actions={<>
                <ExportMenu filename={`${invoice.number}-line-items`} title={`${invoice.number} line items`} columns={lineItemExportColumns} rows={lineItemExportRows} sheetName="Line items" testId="invoice-line-items-export" />
                <StatusPill status={invoice.status} />
            </>} />
            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card className="border-border">
                        <table className="min-w-full text-sm">
                            <thead className="bg-muted/50 text-left">
                                <tr>
                                    <th className="py-2 px-3">Description</th>
                                    <th className="py-2 px-3 text-right">Qty</th>
                                    <th className="py-2 px-3 text-right">Unit</th>
                                    <th className="py-2 px-3 text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {invoice.line_items.map((li, i) => (
                                    <tr key={`line-${i}-${li.description}`}>
                                        <td className="py-2 px-3">{li.description}</td>
                                        <td className="py-2 px-3 text-right tabular-nums">{li.qty}</td>
                                        <td className="py-2 px-3 text-right tabular-nums">{formatMoney(li.unit_price)}</td>
                                        <td className="py-2 px-3 text-right tabular-nums">{formatMoney(li.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-2">Payments</h3>
                        <ul className="divide-y divide-border">
                            {payments.length === 0 && <li className="py-2 text-sm text-muted-foreground">No payments yet.</li>}
                            {payments.map((p) => (
                                <li key={p.id} className="py-2 flex items-center justify-between text-sm">
                                    <div>
                                        <div className="font-medium">{paymentLabel(p)} · {p.method}</div>
                                        <div className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</div>
                                    </div>
                                    <div className={`tabular-nums ${p.amount < 0 ? "text-red-700" : ""}`}>{formatMoney(p.amount)}</div>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>
                <div className="space-y-6">
                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Booking context</h3>
                        {bookingDetail ? <ul className="space-y-2 text-sm"><li className="flex justify-between gap-3"><span>Location</span><span className="font-medium">{location?.name || "Not set"}</span></li><li className="flex justify-between gap-3"><span>Room</span><span className="font-medium">{bookingDetail.room?.name || "Not set"}</span></li><li className="flex justify-between gap-3"><span>Service</span><span className="font-medium">{bookingDetail.service?.name || "Not set"}</span></li><li className="flex justify-between gap-3"><span>Recording hours</span><span className="font-medium">{durationHours(bookingDetail.booking.start_utc, bookingDetail.booking.end_utc)}h</span></li><li className="flex justify-between gap-3"><span>Session</span><span className="font-medium">{fmtDateTime(bookingDetail.booking.start_utc)}</span></li></ul> : <p className="text-sm text-muted-foreground">This invoice is not linked to a booking.</p>}
                    </Card>
                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Recent client activity</h3>
                        <ul className="space-y-3">{clientActivity.length === 0 && <li className="text-sm text-muted-foreground">No recent activity for this client.</li>}{clientActivity.slice(0, 6).map((item) => <li key={item.id} className="text-sm"><div className="font-medium">{item.subject || String(item.type || item.action || "Activity").replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{fmtDateTime(item.created_at)}{item.actor_name ? ` · ${item.actor_name}` : ""}</div>{(item.body || item.metadata?.text) && <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.body || item.metadata.text}</div>}</li>)}</ul>
                    </Card>
                    <Card className="p-4 border-border">
                        <h3 className="font-display text-base font-semibold mb-3">Invoice totals</h3>
                        <ul className="space-y-1 text-sm tabular-nums">
                            <li className="flex justify-between"><span>Subtotal</span><span>{formatMoney(invoice.subtotal)}</span></li>
                            <li className="flex justify-between"><span>Tax ({invoice.tax_pct}%)</span><span>{formatMoney(invoice.tax)}</span></li>
                            <li className="flex justify-between"><span>Discount</span><span>–{formatMoney(invoice.discount)}</span></li>
                            <li className="flex justify-between font-semibold"><span>Total</span><span>{formatMoney(invoice.total)}</span></li>
                            <li className="flex justify-between"><span>Paid</span><span>{formatMoney(invoice.paid)}</span></li>
                            <li className="flex justify-between text-primary"><span>Balance</span><span>{formatMoney(invoice.balance)}</span></li>
                        </ul>
                        <div className="space-y-2 mt-3">
                            {invoice.deposit_required > invoice.deposit_paid && (
                                <Button className="w-full" onClick={() => act(`/invoices/${invoice.id}/mark-paid`, { is_deposit: true })} data-testid="invoice-mark-deposit">Record deposit ({formatMoney(invoice.deposit_required - invoice.deposit_paid)})</Button>
                            )}
                            {invoice.balance > 0 && (
                                <Button variant="outline" className="w-full" onClick={() => act(`/invoices/${invoice.id}/mark-paid`, { amount: invoice.balance })} data-testid="invoice-mark-full">Mark fully paid</Button>
                            )}
                            {invoice.paid > 0 && (
                                <Button variant="ghost" className="w-full text-red-700" onClick={() => act(`/invoices/${invoice.id}/refund`, {})} data-testid="invoice-refund">Issue refund</Button>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}
